/**
 * Group BIM/CAD deliverable files by extension for scannable lists.
 * Shared by the operator file library and the public vendor upload page.
 */

import { cadBimLoadPriority, fileExtension } from "@/lib/virtualspace/cad-bim-extensions";
import { compareBimCadFileOrder } from "@/lib/virtualspace/bim-file-sort";
import { archiveDisplayBaseName } from "@/lib/virtualspace/deliverable-archive-naming";

export type BimCadFileOrderFields = {
  file_name?: string | null;
  fileName?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  display_sort_key?: number | null;
};

export type BimFileTypeGroup<T> = {
  ext: string;
  label: string;
  files: T[];
};

export function fileHasDisplaySortKey(
  displaySortKey: number | null | undefined,
): boolean {
  return displaySortKey != null && Number.isFinite(Number(displaySortKey));
}

/**
 * Group a flat file list by extension. Operators almost always ship
 * mixed-format bundles (RVT + DWG + PDF), so a long flat list fights
 * the eye even when sorted.
 *
 * When any row carries `display_sort_key`, returns one manual-order
 * group (matches operator library + VirtualSpace rails).
 */
export function groupBimCadFilesByType<T>(
  files: T[],
  toOrderFields: (file: T) => BimCadFileOrderFields,
): BimFileTypeGroup<T>[] {
  const anyKeyed = files.some((f) =>
    fileHasDisplaySortKey(toOrderFields(f).display_sort_key),
  );
  if (anyKeyed) {
    const sorted = [...files].sort((a, b) =>
      compareBimCadFileOrder(toOrderFields(a), toOrderFields(b)),
    );
    return [{ ext: "__manual__", label: "Files", files: sorted }];
  }

  const map = new Map<string, T[]>();
  for (const f of files) {
    const name = archiveDisplayBaseName(
      toOrderFields(f).file_name ?? toOrderFields(f).fileName ?? "",
    );
    const ext = fileExtension(name) || "other";
    const list = map.get(ext) ?? [];
    list.push(f);
    map.set(ext, list);
  }

  const groups = Array.from(map.entries()).map(([ext, list]) => ({
    ext,
    label: ext === "other" ? "Other" : ext.toUpperCase(),
    files: [...list].sort((a, b) =>
      compareBimCadFileOrder(toOrderFields(a), toOrderFields(b)),
    ),
  }));

  groups.sort((a, b) => {
    const pa = a.ext === "other" ? 0 : cadBimLoadPriority(`x.${a.ext}`);
    const pb = b.ext === "other" ? 0 : cadBimLoadPriority(`x.${b.ext}`);
    if (pa !== pb) return pb - pa;
    return a.ext.localeCompare(b.ext);
  });

  return groups;
}

/** True when grouping would show 2+ type buckets (hide redundant single-type header). */
export function bimFileGroupsShouldShowTypeHeaders<T>(
  groups: BimFileTypeGroup<T>[],
): boolean {
  return groups.length > 1 && groups[0]?.ext !== "__manual__";
}
